import { ID } from '@adornis/baseql/baseqlTypes.js';
import { Arg, Entity, Field, Query, Subscription } from '@adornis/baseql/decorators.js';
import { MongoEntity } from '@adornis/baseql/entities/mongoEntity.js';
import { getCollection, getCollectionHandle } from '@adornis/baseql/server/collections.js';
import type { BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { validate } from '@adornis/validation/decorators.js';
import { nonOptional } from '@adornis/validation/functions/nonOptional.js';
import { DateTime } from 'luxon';

@Entity()
export class Timeframe extends MongoEntity {
  static override _class = 'Timeframe';
  static override _collectionName = 'timeframes';

  @validate(nonOptional())
  @Field(type => String)
  public name!: string;

  @validate(nonOptional())
  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  public from!: DateTime;

  @validate(nonOptional())
  @Field(type => DateTime, { default: v => v ?? DateTime.now() })
  public till!: DateTime;

  @validate(nonOptional())
  @Field(type => ID)
  public institutionID!: ID;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  public isActive!: boolean;

  @validate(nonOptional())
  @Field(type => DateTime)
  publishDate?: DateTime;

  @validate(nonOptional({ allowFalsy: true }))
  @Field(type => Boolean, { default: v => v ?? false })
  public isRegistrationPossibleBesidesTimeframe!: boolean;

  @Query(type => [Timeframe])
  static getTimeframesByInstitution(@Arg('institutionID', type => ID) institutionID: ID) {
    return async (gqlFields: BaseQLSelectionSet<Timeframe>) => {
      const collection = await getCollection<Timeframe>(Timeframe._class);
      const result = await collection.find<Timeframe>({ institutionID }).toArray();
      return result;
    };
  }

  @Subscription(type => [Timeframe])
  static subscribeTimeframesByInstitution(
    @Arg('institutionID', type => ID) institutionID: ID,
    @Arg('exceptTimeframeID', type => ID) exceptTimeFrameID?: string,
  ) {
    return (gqlFields: BaseQLSelectionSet<Timeframe>) => {
      return getCollectionHandle<Timeframe>(Timeframe._collectionName).watchQuery({
        institutionID,
        ...(exceptTimeFrameID ? { _id: { $ne: exceptTimeFrameID } } : {}),
      });
    };
  }
}
